import { api } from ".";

export interface IContractInsightsRequest {
  accountId: string;
  /** Provider id to scope the analysis to a single provider. */
  providerId: string;
}

export interface ILowPayingPayerOpportunity {
  payerId: number;
  payerName: string;
  providerAvgRate: number;
  competitorAvgRate: number;
  percentageDifference: number;
  recommendation: string;
}

export interface IImplantReimbursementOpportunity {
  payerId: number;
  payerName: string;
  hasImplantCoverage: boolean;
  recommendation: string;
}

export interface IMPDRComparisonOpportunity {
  payerId: number;
  payerName: string;
  averageDiscount: number;
  highestDiscountRank: string;
  recommendation: string;
}

export interface IBelowMedicareOpportunity {
  payerId: number;
  payerName: string;
  avgRate: number;
  medicareRate: number;
  percentageDifference: number;
  recommendation: string;
}

export interface IContractInsightsRecommendations {
  lowPayingPayerOpportunities: ILowPayingPayerOpportunity[];
  implantReimbursementOpportunities: IImplantReimbursementOpportunity[];
  mpdrComparisonOpportunities: IMPDRComparisonOpportunity[];
  belowMedicareOpportunities: IBelowMedicareOpportunity[];
}

export interface IPayerImplantCoverage {
  payerId: number;
  payerName: string;
  totalProcedures: number;
  implantProcedures: number;
  implantCoveragePercentage: number;
}

export interface IPayerRateComparison {
  payerId: number;
  payerName: string;
  providerAvgRate: number;
  competitorAvgRate?: number;
  averageCommercialRate?: number;
  cptCount: number;
  percentageDifferenceVsCompetitors?: number;
  percentageDifferenceVsCommercial?: number;
  qualifiesForRecommendation: boolean;
  totalVolume?: number;
  competitorCount?: number;
}

export interface ILowPayingPayerAuditData {
  dataSource:
    | "market_prices"
    | "internal_comparison"
    | "market_prices_with_internal_fallback";
  providerPayerCount: number;
  marketPayerCount?: number;
  calculationMethod: string;
  averageRateCalculation?: {
    totalRevenue: number;
    totalCptCount: number;
    weightedAverage: number;
  };
  varianceCalculation?: {
    lowestRate: number;
    highestRate: number;
    variancePercentage: number;
  };
  cptCodesAnalyzed: number;
  minimumCptThreshold: number;
  topCptCodesAnalyzed?: string[];
  allPayerComparisons: IPayerRateComparison[];
  marketDataAnalyzed?: boolean;
  highOpportunitiesFound?: boolean;
  fallbackReason?: string;
}

export interface IPayerImplantDetails {
  payerId: number;
  payerName: string;
  totalProcedures: number;
  implantProcedures: number;
  implantCoveragePercentage: number;
  cptCodesWithImplantCoverage: string[];
}

export interface IImplantReimbursementAuditData {
  totalCommercialPayers: number;
  payersWithImplantCoverage: number;
  payersWithoutImplantCoverage: number;
  totalImplantProcedures: number;
  excludedPayerTypes: string[];
  inclusionCriteria: string;
  payerImplantDetails: IPayerImplantDetails[];
}

export interface IPayerDiscountStructure {
  rank2: number;
  rank3: number;
  rank4: number;
  rank5Plus: number;
}

export interface IMPDRAuditData {
  totalRulesetsFound: number;
  payersAnalyzed: number;
  discountStructuresFound: Record<string, IPayerDiscountStructure>;
  calculationMethod: string;
}

export interface IBelowMedicareAuditData {
  medicareDataSource: string;
  providersAnalyzed: number;
  cptCodesCompared: number;
  belowMedicareThreshold: number;
  calculationMethod: string;
}

export interface IContractInsightsAuditData {
  lowPayingPayers?: ILowPayingPayerAuditData;
  implantReimbursement?: IImplantReimbursementAuditData;
  mpdrComparison?: IMPDRAuditData;
  belowMedicare?: IBelowMedicareAuditData;
  generationTimeMs: number;
  dataQualityChecks: {
    sufficientData: boolean;
    dataFreshness: string;
    completenessScore: number;
  };
}

export interface IContractInsightsMetadata {
  accountId: string;
  providerId: string;
  hasMarketData: boolean;
  implantDataCount: number;
  ordersAnalyzed: number;
  mpdrRulesetsFound: number;
  payerImplantCoverage: IPayerImplantCoverage[];
  dateGenerated: string;
  auditData: IContractInsightsAuditData;
}

export interface IContractInsightsResponse {
  recommendations: IContractInsightsRecommendations;
  metadata: IContractInsightsMetadata;
}

const contractInsightsQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getContractInsightsRecommendations: builder.mutation<
      IContractInsightsResponse,
      IContractInsightsRequest
    >({
      query: (body) => ({
        url: "/contract-insights/ai-recommendations",
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: IContractInsightsResponse }) =>
        response.data,
      invalidatesTags: ["ContractInsights"],
    }),
  }),
});

export const { useGetContractInsightsRecommendationsMutation } =
  contractInsightsQueries;
